import { useSyncExternalStore } from 'react';

type Listener = () => void;

/**
 * One pair of window listeners for the whole app, fanned out to every consumer.
 *
 * The obvious implementation registers `online`/`offline` inside `subscribe`,
 * which React calls once per mounted consumer — so N consumers means 2N
 * listeners and N redundant store notifications per transition. There is
 * nothing per-consumer about the WebView's network state, so the listeners live
 * here instead and each consumer only joins the set.
 */
const listeners = new Set<Listener>();
let windowListenersAttached = false;

/** Stable identity, so add/removeEventListener always pair up. */
function notifyAllListeners(): void {
  for (const listener of listeners) {
    listener();
  }
}

function attachWindowListeners(): void {
  if (windowListenersAttached || typeof window === 'undefined') return;
  // Both events are needed: `offline` for the drop, `online` for the recovery.
  // Without the second, a consumer that went offline never comes back without
  // an unrelated re-render — the same lag this hook exists to remove.
  window.addEventListener('online', notifyAllListeners);
  window.addEventListener('offline', notifyAllListeners);
  windowListenersAttached = true;
}

function detachWindowListeners(): void {
  if (!windowListenersAttached || typeof window === 'undefined') return;
  window.removeEventListener('online', notifyAllListeners);
  window.removeEventListener('offline', notifyAllListeners);
  windowListenersAttached = false;
}

function subscribeToNavigatorOnline(onStoreChange: Listener): () => void {
  listeners.add(onStoreChange);
  attachWindowListeners();
  return () => {
    listeners.delete(onStoreChange);
    // Last consumer out turns off the lights, so an app that unmounts every
    // consumer leaves nothing attached to `window`.
    if (listeners.size === 0) {
      detachWindowListeners();
    }
  };
}

/**
 * Only a POSITIVE `false` counts as offline. An absent `navigator` (SSR, a test
 * environment without a DOM) is not evidence of anything, so it reads as online
 * — matching the `navigator.onLine === false` guard every call site used before
 * this hook existed.
 *
 * Exported for direct unit test: the no-`navigator` branch cannot be reached
 * through `renderHook` in jsdom, where `navigator` always exists.
 */
export function getNavigatorOnlineSnapshot(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine !== false;
}

/**
 * Server render has no network state to report, so it must never start out
 * offline: a `false` here would render the offline banner into the HTML and
 * then hydrate into a mismatch the moment the client disagreed.
 */
export function getNavigatorOnlineServerSnapshot(): boolean {
  return true;
}

/**
 * Reactive `navigator.onLine`.
 *
 * Reading `navigator.onLine` directly during render samples it once and never
 * hears about the change: React has no idea the value moved, so the component
 * keeps showing the stale answer until something unrelated re-renders it. This
 * hook registers the `online`/`offline` listeners React needs to re-run the
 * component when the WebView's answer actually changes.
 *
 * Platform note: this matters on Windows, where WebView2 reports real
 * transitions. On macOS WKWebView pins `navigator.onLine === true`, so the
 * subscription is inert there — no behaviour change, no regression risk.
 *
 * This hook reports only what the WebView believes. It is NOT the authority on
 * whether Heidi is reachable — that is the Rust connectivity probe behind
 * `offlineConnectivityStateAtom`. Consumers combine the two; see
 * `classifyDesktopConnectivity`.
 */
export function useNavigatorOnline(): boolean {
  return useSyncExternalStore(
    subscribeToNavigatorOnline,
    getNavigatorOnlineSnapshot,
    getNavigatorOnlineServerSnapshot
  );
}
